import { useState, useEffect } from 'react';
import { pickPersonDisplayName, validatePersonName } from '@/lib/people/personDisplayName';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { DownloadMyDataCard } from '@/components/privacy/DownloadMyDataCard';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, User, Lock, Moon, Sun, DollarSign } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Switch } from '@/components/ui/switch';
import { AccountShopMemberships } from '@/components/account/AccountShopMemberships';
import { AccountDangerZone } from '@/components/account/AccountDangerZone';
import { TrainingMonitoringConsentSettings } from '@/components/training/TrainingMonitoringConsentSettings';
import { BillingPanel } from '@/components/billing/BillingPanel';
import { LegalDocumentsPanel } from '@/components/legal/LegalDocumentsPanel';
import { SupportAccessDisclosurePanel } from '@/components/support/SupportAccessDisclosurePanel';
import { ShopSupportAccessPanel } from '@/components/support/ShopSupportAccessPanel';


const accountSchema = z.object({
  displayName: z.string().trim().min(1, 'Display name is required').max(100, 'Name must be less than 100 characters'),
  currentPassword: z.string().optional(),
  newPassword: z.string().min(8, 'Password must be at least 8 characters').optional().or(z.literal('')),
  confirmPassword: z.string().optional(),
}).refine((data) => {
  if (data.newPassword && data.newPassword !== data.confirmPassword) {
    return false;
  }
  return true;
}, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type AccountValues = z.infer<typeof accountSchema>;

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, activeShop } = useAuth();
  const { canManageBilling } = usePermissions();
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, default to dark
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return true; // Default to dark
  });

  const form = useForm<AccountValues>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      // Never pre-fill the email as a name: saving it would make the address
      // the person's display name everywhere the shop sees them.
      displayName: pickPersonDisplayName({ profileDisplayName: user?.user_metadata?.display_name ?? null, email: user?.email ?? null }) ?? '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: AccountValues) => {
    setIsSaving(true);
    try {
      const trimmedName = values.displayName.trim();
      const nameError = validatePersonName(trimmedName, user?.email);
      if (nameError) {
        form.setError('displayName', { message: nameError });
        return;
      }

      // Update display name in auth metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { display_name: trimmedName }
      });
      if (updateError) throw updateError;

      // Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ display_name: trimmedName })
        .eq('id', user?.id);
      if (profileError) throw profileError;

      // Sync to passport_profiles (if exists)
      await supabase
        .from('passport_profiles')
        .update({ display_name: trimmedName })
        .eq('user_id', user?.id);

      // Sync to technician_profiles (if exists)
      await supabase
        .from('technician_profiles')
        .update({ full_name: trimmedName })
        .eq('user_id', user?.id);

      // Update password if provided
      if (values.newPassword && values.newPassword.trim() !== '') {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: values.newPassword
        });

        if (passwordError) throw passwordError;

        toast({
          title: 'Account updated',
          description: 'Your account details and password have been updated successfully',
        });

        form.setValue('currentPassword', '');
        form.setValue('newPassword', '');
        form.setValue('confirmPassword', '');
        setIsChangingPassword(false);
      } else {
        toast({
          title: 'Account updated',
          description: 'Your account details have been updated successfully',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error updating account',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleTheme = (checked: boolean) => {
    setIsDarkMode(checked);
    const theme = checked ? 'dark' : 'light';
    localStorage.setItem('theme', theme);
    
    if (checked) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Apply theme on mount
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopBar />
      
      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Account Settings</h1>
              <p className="text-muted-foreground mt-1">Manage your account details and preferences</p>
            </div>
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        Profile Information
                      </CardTitle>
                      <CardDescription>Update your profile details</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Email</label>
                        <Input value={user?.email || ''} disabled className="bg-muted" />
                        <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                      </div>

                      <FormField
                        control={form.control}
                        name="displayName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Display Name</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Your name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        Password
                      </CardTitle>
                      <CardDescription>Change your password</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {!isChangingPassword ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsChangingPassword(true)}
                        >
                          Change Password
                        </Button>
                      ) : (
                        <>
                          <FormField
                            control={form.control}
                            name="newPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>New Password</FormLabel>
                                <FormControl>
                                  <PasswordInput {...field} placeholder="Enter new password" autoComplete="new-password" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Confirm New Password</FormLabel>
                                <FormControl>
                                  <PasswordInput {...field} placeholder="Confirm new password" autoComplete="new-password" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => {
                              setIsChangingPassword(false);
                              form.setValue('newPassword', '');
                              form.setValue('confirmPassword', '');
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </CardContent>
                  </Card>


                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {isDarkMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                        Theme
                      </CardTitle>
                      <CardDescription>
                        Choose between light and dark theme
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium">Dark Mode</div>
                          <div className="text-sm text-muted-foreground">
                            Use dark theme for better visibility in low-light environments
                          </div>
                        </div>
                        <Switch
                          checked={isDarkMode}
                          onCheckedChange={toggleTheme}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Shop Memberships */}
                  <AccountShopMemberships />

                  {/* CORE-BK responsibility 8 — the person may obtain what is
                      held about them. Scoped to auth.uid() server-side; there
                      is deliberately no subject-identifier input. */}
                  <DownloadMyDataCard
                    subjectType="account"
                    invoke={(body) => supabase.functions.invoke('data-subject-export', { body })}
                  />

                  {/* Legal Documents */}
                  <LegalDocumentsPanel />

                  {/* Billing & Subscription */}
                  {canManageBilling && activeShop && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5" />
                          Billing & Subscription
                        </CardTitle>
                        <CardDescription>
                          Manage your subscription and payment methods.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <BillingPanel shopId={activeShop.id} />
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isSaving}>
                      {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </Button>
                  </div>
                </form>
              </Form>

              {/* Training Companion monitoring consent - outside the form (self-managed) */}
              <div className="mt-6">
                <TrainingMonitoringConsentSettings />
              </div>

              {/* CORE-BJ responsibility 6: passive disclosure of privileged
                  support access. Read-only and self-managed, so it sits outside
                  the form alongside the other self-managed panels. */}
              <div className="mt-6">
                <SupportAccessDisclosurePanel />
              </div>

              {/* Core-100 R5 CORE-BJ / OD-49: the shop's own view of shop-authorised support access — every member sees
                  it; owners/managers approve, deny or revoke (the server decides who may). */}
              {activeShop && (
                <div className="mt-6">
                  <ShopSupportAccessPanel shopId={activeShop.id} />
                </div>
              )}

              {/* Danger Zone - outside the form */}
              <div className="mt-6">
                <AccountDangerZone />
              </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettings;
